import type { ComponentType, SVGProps } from 'react'
import { ExternalLink, Globe, Mail, Phone } from 'lucide-react'

import {
  buildFacebookHref,
  buildGitHubHref,
  buildInstagramHref,
  buildLinkedInHref,
  buildMailtoHref,
  buildSubstackHref,
  buildTelHref,
  buildThreadsHref,
  buildTikTokHref,
  buildWebsiteHref,
  buildXHref,
  buildYouTubeHref,
  formatFacebookDisplay,
  formatGitHubDisplay,
  formatInstagramDisplay,
  formatLinkedInDisplay,
  formatSubstackDisplay,
  formatThreadsDisplay,
  formatTikTokDisplay,
  formatWebsiteDisplay,
  formatXDisplay,
  formatYouTubeDisplay,
  normalizeSocialText,
} from '@/lib/social-contact'
import {
  SOCIAL_NETWORK_LABEL,
  type SocialContactNetwork,
} from '@/lib/social-networks'
import { cn } from '@/lib/utils'

import { getBrandSocialMonoIcon } from './brand-social-icons'

/** Muted contact links: icon unchanged on hover; label underlines only. shrink-0 keeps the whole chip together when the strip wraps. */
const CONTACT_LINK_CLASS =
  'group inline-flex min-w-0 max-w-full shrink-0 items-center gap-2 text-base text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm'

const CONTACT_LABEL_CLASS =
  'min-w-0 truncate text-muted-foreground underline-offset-4 group-hover:underline'

type ContactIcon = ComponentType<SVGProps<SVGSVGElement>>

type EntityContactLinkProps = {
  href: string
  icon: ContactIcon
  label: string
  title?: string
  ariaLabel: string
  className?: string
  labelClassName?: string
  external?: boolean
}

function EntityContactLink({
  href,
  icon: Icon,
  label,
  title,
  ariaLabel,
  className,
  labelClassName,
  external = false,
}: EntityContactLinkProps) {
  return (
    <a
      href={href}
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      className={cn(CONTACT_LINK_CLASS, className)}
      title={title ?? label}
      aria-label={ariaLabel}
    >
      <Icon className="h-4 w-4 shrink-0" aria-hidden />
      <span className={cn(CONTACT_LABEL_CLASS, labelClassName)}>{label}</span>
    </a>
  )
}

const SOCIAL_HREF: Record<SocialContactNetwork, (value: string) => string> = {
  linkedin: buildLinkedInHref,
  x: buildXHref,
  instagram: buildInstagramHref,
  facebook: buildFacebookHref,
  threads: buildThreadsHref,
  tiktok: buildTikTokHref,
  youtube: buildYouTubeHref,
  substack: buildSubstackHref,
  github: buildGitHubHref,
}

const SOCIAL_DISPLAY: Record<SocialContactNetwork, (value: string) => string> = {
  linkedin: formatLinkedInDisplay,
  x: formatXDisplay,
  instagram: formatInstagramDisplay,
  facebook: formatFacebookDisplay,
  threads: formatThreadsDisplay,
  tiktok: formatTikTokDisplay,
  youtube: formatYouTubeDisplay,
  substack: formatSubstackDisplay,
  github: formatGitHubDisplay,
}

export type { SocialContactNetwork }

type SocialContactLinkProps = {
  network: SocialContactNetwork
  value?: string | null
  className?: string
  labelClassName?: string
}

export function SocialContactLink({
  network,
  value,
  className,
  labelClassName,
}: SocialContactLinkProps) {
  const normalized = normalizeSocialText(value)
  if (!normalized) return null

  const href = SOCIAL_HREF[network](normalized)
  const label = SOCIAL_DISPLAY[network](normalized)
  const Icon = getBrandSocialMonoIcon(network)
  const networkLabel = SOCIAL_NETWORK_LABEL[network]

  return (
    <EntityContactLink
      href={href}
      icon={Icon}
      label={label}
      title={normalized}
      ariaLabel={`${networkLabel}: ${normalized}`}
      className={className}
      labelClassName={labelClassName}
      external
    />
  )
}

export function LinkedInContactLink(props: Omit<SocialContactLinkProps, 'network'>) {
  return <SocialContactLink network="linkedin" {...props} />
}

export function XContactLink(props: Omit<SocialContactLinkProps, 'network'>) {
  return <SocialContactLink network="x" {...props} />
}

type EmailContactLinkProps = {
  value?: string | null
  className?: string
  labelClassName?: string
}

export function EmailContactLink({ value, className, labelClassName }: EmailContactLinkProps) {
  const normalized = normalizeSocialText(value)
  if (!normalized) return null

  return (
    <EntityContactLink
      href={buildMailtoHref(normalized)}
      icon={Mail}
      label={normalized}
      ariaLabel={`Email: ${normalized}`}
      className={className}
      labelClassName={labelClassName}
    />
  )
}

type PhoneContactLinkProps = {
  value?: string | null
  className?: string
  labelClassName?: string
}

export function PhoneContactLink({ value, className, labelClassName }: PhoneContactLinkProps) {
  const normalized = normalizeSocialText(value)
  if (!normalized) return null

  return (
    <EntityContactLink
      href={buildTelHref(normalized)}
      icon={Phone}
      label={normalized}
      ariaLabel={`Phone: ${normalized}`}
      className={className}
      labelClassName={labelClassName}
    />
  )
}

type WebsiteContactLinkProps = {
  value?: string | null
  className?: string
  labelClassName?: string
  /** Public company page: “Visit site” + external-link icon instead of the hostname. */
  presentation?: 'hostname' | 'visitSite'
}

export function WebsiteContactLink({
  value,
  className,
  labelClassName,
  presentation = 'hostname',
}: WebsiteContactLinkProps) {
  const normalized = normalizeSocialText(value)
  if (!normalized) return null

  const visitSite = presentation === 'visitSite'
  const href = buildWebsiteHref(normalized)

  return (
    <EntityContactLink
      href={href}
      icon={visitSite ? ExternalLink : Globe}
      label={visitSite ? 'Visit site' : formatWebsiteDisplay(normalized)}
      title={normalized}
      ariaLabel={visitSite ? `Visit site: ${normalized}` : `Website: ${normalized}`}
      className={className}
      labelClassName={labelClassName}
      external
    />
  )
}
